/**
 * Platform-agnostic theme tokens — the source of truth for the theme
 * names and the single brand accent each theme contributes.
 *
 * The web apps consume the full token set as CSS (`theme.css`, imported
 * via `@myosh/design-system/styles`); this module carries the slice that
 * non-CSS consumers (React Native) need: the accent (`--primary`) per
 * theme and colour scheme, byte-identical to `theme.css` and pinned to
 * it by test. No DOM access — safe to import anywhere.
 */

export const THEME_NAMES = ['blue', 'brown', 'green', 'grey', 'orange', 'purple', 'red', 'yellow'] as const;

export type ThemeName = (typeof THEME_NAMES)[number];

export const DEFAULT_THEME: ThemeName = 'blue';

/** Narrow an arbitrary string (e.g. the backend `company-theme`) to a known theme. */
export const toThemeName = (value: string | undefined | null): ThemeName => {
  return THEME_NAMES.includes(value as ThemeName) ? (value as ThemeName) : DEFAULT_THEME;
};

export interface ThemeAccent {
  /** `--primary` exactly as written in theme.css, per colour scheme. */
  oklch: { light: string; dark: string };
  /**
   * Ink for text/icons rendered on the accent, chosen for contrast.
   * Not part of the web CSS (web composes its own foregrounds);
   * defined here so non-CSS consumers agree on it.
   */
  foreground: { light: string; dark: string };
}

export const THEME_ACCENTS: Record<ThemeName, ThemeAccent> = {
  blue: {
    oklch: { light: 'oklch(0.42 0.16 260)', dark: 'oklch(0.66 0.18 260)' },
    foreground: { light: '#ffffff', dark: '#000000' },
  },
  brown: {
    oklch: { light: 'oklch(0.48 0.03 62)', dark: 'oklch(0.66 0.03 62)' },
    foreground: { light: '#ffffff', dark: '#000000' },
  },
  green: {
    oklch: { light: 'oklch(0.48 0.15 145)', dark: 'oklch(0.66 0.2 145)' },
    foreground: { light: '#ffffff', dark: '#000000' },
  },
  grey: {
    oklch: { light: 'oklch(0.48 0 0)', dark: 'oklch(0.66 0 0)' },
    foreground: { light: '#ffffff', dark: '#000000' },
  },
  orange: {
    oklch: { light: 'oklch(0.7 0.16 57)', dark: 'oklch(0.66 0.18 57)' },
    foreground: { light: '#ffffff', dark: '#000000' },
  },
  purple: {
    oklch: { light: 'oklch(0.48 0.2 297)', dark: 'oklch(0.68 0.2 297)' },
    foreground: { light: '#ffffff', dark: '#000000' },
  },
  red: {
    oklch: { light: 'oklch(0.48 0.19 29)', dark: 'oklch(0.68 0.2 29)' },
    foreground: { light: '#ffffff', dark: '#000000' },
  },
  yellow: {
    oklch: { light: 'oklch(0.85 0.17 90)', dark: 'oklch(0.9 0.12 90)' },
    foreground: { light: '#000000', dark: '#000000' },
  },
};
